export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const TYPE_COLORS: Record<
  string,
  { bg: string; border: string; text: string; dot: string }
> = {
  festival: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
    dot: "bg-orange-500",
  },
  vrat: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
    dot: "bg-green-500",
  },
  eclipse: {
    bg: "bg-purple-50",
    border: "border-purple-200",
    text: "text-purple-700",
    dot: "bg-purple-500",
  },
  tithi_based: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    dot: "bg-blue-500",
  },
};

const TYPE_LABELS: Record<string, string> = {
  festival: "Festival",
  vrat: "Vrat",
  eclipse: "Eclipse",
  tithi_based: "Tithi",
  sankranti: "Sankranti",
};

/** The engine row type as a reader sees it ("tithi_based" reads "Tithi"). */
export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type.charAt(0).toUpperCase() + type.slice(1);
}

// The vrat-year's Sankranti rows (the Sun entering a sign) are tithi-like
// monthly markers, not festivals: they share the tithi colour.
const TYPE_COLOR_ALIASES: Record<string, string> = { sankranti: "tithi_based" };

export function getTypeColor(type: string) {
  return TYPE_COLORS[TYPE_COLOR_ALIASES[type] ?? type] || TYPE_COLORS.festival;
}

// The calendar now renders on the server too, so a date must read the same in
// Node and in every browser: built from the digits, never toLocaleDateString
// (ICU builds differ, e.g. "Sep" vs "Sept"). No weekday: the live correctness
// check reads a weekday-led date near the top of <main> as the page's day.
function dateParts(dateStr: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(match[1]), month, day: Number(match[3]) };
}

/** "8 Nov". */
export function formatDateShort(dateStr: string): string {
  const parts = dateParts(dateStr);
  if (!parts) return dateStr;
  return `${parts.day} ${MONTH_NAMES[parts.month - 1].slice(0, 3)}`;
}

/** "8 November 2026" (no weekday). */
export function formatDateLong(dateStr: string): string {
  const parts = dateParts(dateStr);
  if (!parts) return dateStr;
  return `${parts.day} ${MONTH_NAMES[parts.month - 1]} ${parts.year}`;
}

/** "25 October" (no year, no weekday). */
export function formatDayMonth(dateStr: string): string {
  const parts = dateParts(dateStr);
  if (!parts) return dateStr;
  return `${parts.day} ${MONTH_NAMES[parts.month - 1]}`;
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month - 1, 1).getDay();
}
